import React, {useEffect, useRef, useState} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {FirebaseError} from "firebase/app";
import {ConfirmationResult} from "firebase/auth";
import {
  clearPhoneRecaptcha,
  confirmPhoneSignIn,
  createAccountWithEmail,
  getLoggedInUserOrEmpty,
  isAccountConflictError,
  preparePhoneRecaptcha,
  provisionUser,
  signInWithEmail,
  signOutUser,
  signUpCompany,
  startPhoneSignIn,
} from "../../shared/services/auth.service";
import {logError, logInfo, setLogContext} from "../../shared/services/logger.service";
import {firebaseAuth} from "../../shared/services/firebase.service";
import {toE164} from "../../shared/phone";
import {acceptanceOf, getLegalDocuments, LegalDocumentsDto} from "../../shared/services/legal.service";
import LegalAgreementCheckbox from "../../components/legal/LegalAgreementCheckbox";

const RECAPTCHA_ID = 'phone-signup-recaptcha';

/**
 * SQ-537: a tree service creates its own company account. Phone is the
 * default path (name, company, phone, text code); email + password is the
 * other. Nothing here requires an email.
 */
const friendlyError = (error: unknown): string => {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/email-already-in-use':
        return 'An account already uses this email. Sign in instead.';
      case 'auth/weak-password':
        return 'Choose a password of at least 6 characters.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/invalid-phone-number':
      case 'auth/missing-phone-number':
        return 'Please enter a valid phone number.';
      case 'auth/invalid-verification-code':
        return 'That code is incorrect. Try again.';
      case 'auth/code-expired':
        return 'That code expired. Send a new one.';
      case 'auth/captcha-check-failed':
        return 'Phone verification failed. Please try again.';
      case 'auth/quota-exceeded':
        return 'Too many phone sign-in attempts. Please try again later.';
      case 'auth/operation-not-allowed':
        return 'Phone sign-in is not enabled. Ask an administrator to turn it on.';
      case 'auth/invalid-app-credential':
      case 'auth/app-not-authorized':
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
          return 'Firebase does not send phone codes from localhost. Use the local test number (208) 555-0100 / 123456, or open the app from 127.0.0.1 or staging.savaquan.com.';
        }
        return 'Phone verification was rejected for this site. Try again from the app’s web address.';
      default:
        return 'Unable to create the account. Please try again.';
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Unable to create the account. Please try again.';
};

const SignupPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'phone' | 'email'>('phone');
  const [companyName, setCompanyName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  // SQ-419: no account without agreeing to the current documents.
  const [legalDocuments, setLegalDocuments] = useState<LegalDocumentsDto | null>(null);
  const [agreed, setAgreed] = useState(false);
  const recaptchaReady = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getLegalDocuments()
      .then((docs) => { if (!cancelled) setLegalDocuments(docs); })
      .catch((e) => {
        logError('Failed to load legal documents', {}, e as Error);
        if (!cancelled) setError('Could not load the Terms of Service. Reload the page to try again.');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Only the session restored at page load is judged here, never the
    // sign-in this page makes mid-signup (an auth-state listener would see
    // the fresh phone account, find it has no company yet, and sign it
    // out under the signup request). A restored session that already has
    // a company has nothing to create; one without (a half-finished
    // signup) is signed out so the form starts clean.
    let cancelled = false;
    firebaseAuth.authStateReady().then(async () => {
      if (cancelled) {
        return;
      }
      if (!firebaseAuth.currentUser) {
        setCheckingSession(false);
        return;
      }
      try {
        await provisionUser(navigate);
        navigate('/home', {replace: true});
      } catch {
        await signOutUser().catch(() => undefined);
        if (!cancelled) {
          setCheckingSession(false);
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (checkingSession || recaptchaReady.current) {
      return;
    }
    try {
      preparePhoneRecaptcha(RECAPTCHA_ID);
      recaptchaReady.current = true;
    } catch (e) {
      logError('Phone recaptcha setup failed', {}, e as Error);
    }
    return () => {
      clearPhoneRecaptcha();
      recaptchaReady.current = false;
    };
  }, [checkingSession]);

  const detailsValid = companyName.trim().length > 0 && ownerName.trim().length > 0
    && agreed && legalDocuments != null;

  // The Firebase account exists and is signed in: make the company.
  const completeSignup = async () => {
    if (!legalDocuments || !agreed) {
      throw new Error('Agree to the Terms of Service and Privacy Policy to create an account.');
    }
    try {
      await signUpCompany(navigate, {
        companyName: companyName.trim(),
        ownerName: ownerName.trim(),
        email: mode === 'phone' && email.trim() ? email.trim() : null,
        phoneNumber: mode === 'email' && phone.trim() ? phone.trim() : null,
        legal: acceptanceOf(legalDocuments),
      });
    } catch (e) {
      if (isAccountConflictError(e)) {
        // The account is already on a team: provisioning signs them in to it.
        try {
          await provisionUser(navigate);
        } catch {
          await signOutUser().catch(() => undefined);
          throw e;
        }
      } else {
        await signOutUser().catch(() => undefined);
        throw e;
      }
    }
    setLogContext({user: getLoggedInUserOrEmpty()});
    logInfo('Signup successful');
    navigate('/home');
  };

  const handleSendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const e164 = toE164(phone);
    if (!e164) {
      setError('Please enter a valid phone number.');
      return;
    }
    setSubmitting(true);
    try {
      if (!recaptchaReady.current) {
        preparePhoneRecaptcha(RECAPTCHA_ID);
        recaptchaReady.current = true;
      }
      const result = await startPhoneSignIn(e164);
      setConfirmation(result);
      setCode('');
    } catch (e) {
      recaptchaReady.current = false;
      try {
        preparePhoneRecaptcha(RECAPTCHA_ID);
        recaptchaReady.current = true;
      } catch {
        /* next attempt will recreate */
      }
      logError('Signup phone code send error', {}, e as Error);
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!confirmation) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await confirmPhoneSignIn(confirmation, code.trim());
      await completeSignup();
    } catch (e) {
      logError('Signup phone code confirm error', {}, e as Error);
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      try {
        await createAccountWithEmail(email.trim(), password);
      } catch (e) {
        // An email account that exists but has no company yet may still
        // create one: sign it in with the given password.
        if (e instanceof FirebaseError && e.code === 'auth/email-already-in-use') {
          await signInWithEmail(email.trim(), password);
        } else {
          throw e;
        }
      }
      await completeSignup();
    } catch (e) {
      logError('Email signup error', {}, e as Error);
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
        <Box sx={{width: '100%', maxWidth: 400}}>
          <Typography variant="body1" align="center" gutterBottom>
            One moment…
          </Typography>
          <LinearProgress />
        </Box>
      </Box>
    );
  }

  const legalCheckbox = (
    <LegalAgreementCheckbox
      documents={legalDocuments}
      checked={agreed}
      onChange={setAgreed}
      disabled={submitting}
    />
  );

  const detailFields = (
    <>
      <TextField
        label="Company name"
        fullWidth
        margin="normal"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        autoComplete="organization"
        required
      />
      <TextField
        label="Your name"
        fullWidth
        margin="normal"
        value={ownerName}
        onChange={(e) => setOwnerName(e.target.value)}
        autoComplete="name"
        required
      />
    </>
  );

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
      <Paper elevation={3} sx={{p: 4, width: '100%', maxWidth: 400}}>
        <Typography variant="h5" align="center" gutterBottom>
          Create your SavaQuan account
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 2}}>
          {mode === 'phone'
            ? 'We text a code to your phone. No email or password needed.'
            : 'Sign in with an email and password instead of a phone.'}
        </Typography>

        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

        <Box id={RECAPTCHA_ID} />

        {mode === 'phone' && confirmation ? (
          <Box component="form" onSubmit={handleConfirmCode}>
            <Typography color="text.secondary" sx={{mb: 1}}>
              Enter the code sent to {phone}.
            </Typography>
            <TextField
              label="Verification code"
              fullWidth
              margin="normal"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              inputProps={{inputMode: 'numeric'}}
              required
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || !code.trim()}
              sx={{mt: 2}}
            >
              Create account
            </Button>
            <Button
              fullWidth
              disabled={submitting}
              onClick={() => { setConfirmation(null); setCode(''); setError(null); }}
              sx={{mt: 1}}
            >
              Use a different number
            </Button>
          </Box>
        ) : mode === 'phone' ? (
          <Box component="form" onSubmit={handleSendCode}>
            {detailFields}
            <TextField
              label="Phone number"
              type="tel"
              fullWidth
              margin="normal"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              required
            />
            <TextField
              label="Email (optional)"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" color="text.secondary" display="block">
                Localhost cannot receive a real SMS. Use (208) 555-0100, then code 123456.
              </Typography>
            )}
            {legalCheckbox}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || !detailsValid}
              sx={{mt: 2}}
            >
              Send code
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleEmailSignup}>
            {detailFields}
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <TextField
              label="Phone number (optional)"
              type="tel"
              fullWidth
              margin="normal"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
            />
            {legalCheckbox}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || !detailsValid}
              sx={{mt: 2}}
            >
              Create account
            </Button>
          </Box>
        )}

        <Button
          fullWidth
          disabled={submitting}
          onClick={() => {
            setMode(mode === 'phone' ? 'email' : 'phone');
            setError(null);
            setConfirmation(null);
            setCode('');
          }}
          sx={{mt: 1}}
        >
          {mode === 'phone' ? 'Use email and password instead' : 'Use a phone number instead'}
        </Button>

        <Typography variant="body2" align="center" sx={{mt: 2}}>
          Already have an account?{' '}
          <Link component={RouterLink} to="/login">Sign in</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default SignupPage;
